// src/services/localCallRows.js — the browser's own durable copy of a call row.
//
// 🟥 WHY THIS HAS TO EXIST AT ALL. A call's status message is written to
//    `chats/{id}/messages` — and for the phones that document is a RELAY, not
//    storage. ChatModal ingests it into SQLite and then deletes it from
//    Firestore, deliberately and immediately (SWEEPABLE_SYSTEM_TYPES includes
//    'call_history', and `senderOk = isCallHistory ? true : …` removes the
//    "don't sweep somebody else's document" guard for this one type, so ANY
//    member's phone deletes it — including one written by a browser).
//
//    The phones lose nothing: they have SQLite, plus a socket fan-out and a
//    local cache write that both run before the Firestore write. The browser had
//    none of that. Its only store WAS the Firestore document, so the row it
//    wrote appeared for a few seconds, the first phone with that chat open swept
//    it, and the snapshot fired `removed`. That is why a group call left no trace
//    in the web chat — the browser was writing a message into a postbox and
//    calling it an archive.
//
// 🟢 SO THE BROWSER KEEPS ITS OWN, exactly as the phone keeps SQLite. Fed from
//    two directions: the row this browser writes when it hosts a call, and the
//    server's `call-history-sync` broadcast, which reaches every member — so a
//    browser that merely took part in a call gets the row too, without ever
//    depending on a document somebody else's phone is about to delete.
//
// 🟢 Bounded on purpose. Call rows are tiny and rare next to messages, but
//    localStorage is a hard 5MB and shared with everything else the app keeps.

const KEY = 'allo_local_call_rows';
const PER_CHAT = 60;
const MAX_CHATS = 40;

let store = {};
try { store = JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch { store = {}; }
if (!store || typeof store !== 'object') store = {};

const subs = new Set();

function persist() {
  try {
    /* keep the most recently touched chats, and the newest rows within each */
    const chatIds = Object.keys(store);
    if (chatIds.length > MAX_CHATS) {
      const newestOf = (cid) => Math.max(0, ...Object.values(store[cid] || {}).map((r) => r._ts || 0));
      chatIds.sort((a, b) => newestOf(b) - newestOf(a));
      for (const cid of chatIds.slice(MAX_CHATS)) delete store[cid];
    }
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch { /* private mode, or full — the row still lives in memory this session */ }
}

function notify() {
  for (const f of subs) {
    try { f(); } catch { /* one bad subscriber must not stop the others */ }
  }
}

/* 🟥 A FALLBACK MUST NOT MASQUERADE AS KNOWLEDGE. The row's text used to be
   built with 'Group Member' substituted in and then merged like any other
   field — so a copy that knew no name arrived carrying a perfectly non-empty
   string and overwrote the copy that did know one. The name is stored as what
   was actually known (possibly nothing) and the sentence is DERIVED from it, so
   a blank can lose. */
export function rowText(senderName, callType) {
  const who = String(senderName || '').trim() || 'Group Member';
  return `📞 ${who} started a ${callType === 'video' ? 'Video' : 'Voice'} call`;
}

/** Is this value worth writing over one we already have? */
function has(v) {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  return true;
}

/** Field-by-field: the newer copy wins, but only where it actually knows something. */
function pick(oldObj, newObj) {
  const out = { ...(oldObj || {}) };
  for (const [k, v] of Object.entries(newObj || {})) if (has(v)) out[k] = v;
  return out;
}

export function subscribeCallRows(fn) { subs.add(fn); return () => subs.delete(fn); }

/** Every call row this browser has kept for one chat, oldest first. */
export function getCallRows(chatId) {
  if (!chatId) return [];
  const rows = store[chatId];
  if (!rows) return [];
  return Object.values(rows).sort((a, b) => (a._ts || 0) - (b._ts || 0));
}

/**
 * Keep one call row. Idempotent on `id`, so the browser's own write and the
 * server's broadcast of the same call are one row rather than two — which is
 * the whole reason both sides key on `call_end_<sessionId>`.
 */
export function rememberCallRow(chatId, row) {
  if (!chatId || !row || !row.id) return;
  if (!store[chatId]) store[chatId] = {};
  const prev = store[chatId][row.id] || null;
  /* 🟢 A LATER COPY REFINES, IT DOES NOT REPLACE. The host writes first with the
     participant list it holds; the server's record arrives after with the
     authoritative duration. Merging keeps whichever fields each one actually
     knows rather than letting arrival order decide.

     🟥 EXCEPT THE TIME, WHICH IS PINNED TO THE FIRST COPY. The two sources
        stamp the same call moments apart, and `timestamp` is the sort key for
        the whole conversation — so taking the newer one would slide a row the
        reader is already looking at to a different place in the thread. A call
        happened once; it gets one position. */
  const firstTs = (prev && prev._ts) || row._ts || Date.now();
  /* 🟥 A SHALLOW SPREAD IS NOT A MERGE. `{...prev, ...row}` replaces `callData`
     WHOLESALE, so the second copy's blanks overwrite what the first copy knew —
     the server's record carries the authoritative duration but an empty
     participant list and no starter name, and the host's carries the opposite.
     Spreading one over the other therefore loses half the row whichever order
     they arrive in. Merged field by field, with a blank never beating a value. */
  const merged = { ...(prev || {}), ...pick(prev, row) };
  merged.callData = pick(prev && prev.callData, row.callData);
  merged._ts = firstTs;
  merged.timestamp = firstTs;
  /* derived, never merged — see rowText */
  merged.text = rowText(merged.senderName, merged.callData && merged.callData.callType);
  store[chatId][row.id] = merged;

  const ids = Object.keys(store[chatId]);
  if (ids.length > PER_CHAT) {
    const byAge = ids.sort((a, b) => (store[chatId][a]._ts || 0) - (store[chatId][b]._ts || 0));
    for (const id of byAge.slice(0, ids.length - PER_CHAT)) delete store[chatId][id];
  }
  persist();
  notify();
}

/** Build the chat row a finished GROUP call leaves behind, from any source. */
export function callRowFrom({ chatId, sessionId, callType, duration, callInitiatedBy,
  starterName, participants, startedFrom, at, isGroup = true, outcome = 'answered' }) {
  if (!chatId || !sessionId) return null;
  /* stored as what was actually known — empty when nothing was */
  const who = String(starterName || '').trim();
  const ts = Number(at) || Date.now();
  return {
    id: `call_end_${sessionId}`,
    _id: `call_end_${sessionId}`,
    /* 🟢 word for word what both hosts write, so the copy the browser keeps and
       the one that briefly existed in Firestore are the same row to every reader */
    text: rowText(who, callType),
    type: 'system',
    systemType: 'call_history',
    senderId: String(callInitiatedBy || ''),
    senderName: who,
    /* 🟥 THIS WAS HARDCODED TRUE, WHICH IS WHY ONLY GROUP CALLS COULD HAVE A LOCAL
       ROW. A one-to-one call kept nothing on the browser, so its bubble existed
       only in the Firestore document — which store-and-forward sweeps seconds
       later — and in the phone's export. That is the whole of "it appears and
       then disappears until the next sync" for call bubbles. */
    isGroup: !!isGroup,
    timestamp: ts,
    _ts: ts,
    _local: true,
    callData: {
      callType: callType === 'video' ? 'video' : 'voice',
      duration: Math.max(0, Number(duration) || 0),
      /* 🟥 AND `answered: true` WAS HARDCODED TOO. Harmless for a group row, which
         only exists once somebody joined — and wrong for a one-to-one call, where
         missed and declined are most of what there is to say. A row that claims
         every call was answered is worse than no row. */
      answered: String(outcome) === 'answered',
      outcome: String(outcome || 'answered'),
      callInitiatedBy: String(callInitiatedBy || ''),
      participants: Array.isArray(participants) ? participants : [],
      callSessionId: sessionId,
      startedFrom: startedFrom === 'web' ? 'web' : 'app',
      /* 🟥 THE FLAG THE RENDERER ACTUALLY READS. `isGroup` above is a sibling of
         callData, and callInputs reads out of callData — which is exactly how a
         two-person group call came to be rendered as a one-to-one one. Carrying
         it in both places costs nothing and removes the ambiguity. */
      isGroup: !!isGroup,
    },
  };
}
